import { useFetcher } from "@remix-run/react";
import { useCallback, useEffect, useRef, useState } from "react";
import LinkList from "~/ui/LinkList";
import ROUTES from "~/kernel/routing/ROUTES";
import type SerializeType from "~/kernel/server/SerializeType";
import className from "~/kernel/styling/className";
import DateFormat from "~/kernel/dateTime/DateFormat";
import format from "~/kernel/dateTime/format";
import type { Notification } from "@prisma/client";
import IconButton from "~/ui/IconButton";
import PaginationControls from "~/ui/PaginationControls";
import isNil from "~/kernel/validation/isNil";
import useEffectOnce from "~/kernel/effects/useEffectOnce";
import ContextMenuModal from "~/ui/ContextMenuModal";
import parseDate from "~/kernel/dateTime/parseDate";
import minutesToMilliseconds from "~/kernel/dateTime/minutesToMilliseconds";
import useTranslation from "~/kernel/internationalization/useTranslation";
import type { GetNotificationsLoaderData } from "~/routes/notifications/GetNotificationsLoaderData";
import { useRepresentationContext } from "~/kernel/auth/useRepresentationContext";
import { RepresentationType } from "~/kernel/auth/determineRepresentation";

function Notifications({
  notifications,
}: {
  notifications: Pick<
    Notification,
    "id" | "isRead" | "message" | "link" | "createdAt"
  >[];
}) {
  const { t } = useTranslation();

  if (notifications.length > 0) {
    return (
      <div className="max-h-[60vh] overflow-auto">
        <LinkList
          links={notifications.map((notification) => ({
            key: notification.id,
            to: notification.link,
            children: (
              <div>
                <div className="block text-xs text-slate-400">
                  <div
                    className={className("inline-block h-2 w-2 rounded-full", {
                      "bg-blue-500": !notification.isRead,
                      "bg-slate-300": notification.isRead,
                    })}
                  />{" "}
                  {format(notification.createdAt, DateFormat.LongLocalizedDate)}
                </div>
                <p>{notification.message}</p>
              </div>
            ),
          }))}
        ></LinkList>
      </div>
    );
  }

  return <p>{t("notifications.noNotifications")}</p>;
}

function NotificationsPagination({
  pagination,
  onChangePage,
}: {
  pagination:
    | SerializeType<GetNotificationsLoaderData>["pagination"]
    | undefined;
  onChangePage: (page: number) => void;
}) {
  if (isNil(pagination) || pagination.totalCount < 1) {
    return null;
  }

  return (
    <div className="pt-3">
      <PaginationControls
        result={pagination}
        onPrevious={() =>
          pagination.previousPage && onChangePage(pagination.previousPage)
        }
        onNext={() => pagination.nextPage && onChangePage(pagination.nextPage)}
      />
    </div>
  );
}

export default function NotificationsButton() {
  const representation = useRepresentationContext();

  if (representation.type !== RepresentationType.Company) {
    return null;
  }

  return <Content />;
}

function Content() {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [clickEvent, setClickEvent] = useState<React.MouseEvent | undefined>(
    undefined,
  );
  const listFetcher = useFetcher<SerializeType<GetNotificationsLoaderData>>();
  const submitFetcher = useFetcher();
  const serializedNotifications = listFetcher.data?.notifications ?? [];
  const buttonRef = useRef<HTMLButtonElement>(null);
  const notifications = serializedNotifications.map((notification) => ({
    id: notification.id,
    message: notification.message,
    link: notification.link,
    isRead: notification.isRead,
    createdAt: parseDate(notification.createdAt),
    updatedAt: parseDate(notification.updatedAt),
  }));
  const unreadNotifications = notifications.filter((a) => !a.isRead);
  const hasUnreadNotifications = unreadNotifications.length > 0;
  const markNotificationsAsRead = () => {
    submitFetcher.submit(new FormData(), {
      action: ROUTES.markNotificationsAsRead.path(),
      method: "post",
    });
  };

  const fetchNotifications = useCallback(
    (nextPage: number) => {
      listFetcher.load(
        ROUTES.notifications.path({ query: { page: nextPage } }),
      );
    },
    [listFetcher],
  );

  const goToPage = (nextPage: number) => {
    setPage(nextPage);
    fetchNotifications(nextPage);
  };

  useEffectOnce(() => {
    const timeout = setTimeout(() => {
      fetchNotifications(1);
    });
    return () => clearTimeout(timeout);
  });

  useEffect(() => {
    const intervalId = setInterval(
      () => fetchNotifications(page),
      minutesToMilliseconds(1),
    );
    return () => clearInterval(intervalId);
  }, [fetchNotifications, page]);

  return (
    <div>
      <IconButton
        ref={buttonRef}
        aria-label={`${unreadNotifications.length} ${t(
          "notifications.newNotifications",
        )}`}
        onClick={(ev) => {
          setClickEvent(ev);
          setShowContextMenu(true);
        }}
        icon="bell"
      >
        {hasUnreadNotifications && (
          <span className="absolute right-3 z-10 h-2 w-2 rounded-full bg-blue-500"></span>
        )}
      </IconButton>
      <ContextMenuModal
        anchor={buttonRef}
        position="below"
        title={t("notifications.title")}
        show={showContextMenu}
        setShow={(newShow) => {
          setShowContextMenu(newShow);

          if (newShow === false) {
            markNotificationsAsRead();
          }
        }}
        clickEvent={clickEvent}
      >
        <Notifications notifications={notifications} />
        <NotificationsPagination
          pagination={listFetcher.data?.pagination}
          onChangePage={goToPage}
        />
      </ContextMenuModal>
    </div>
  );
}
